import { ChevronRight } from "@mui/icons-material";
import { Icon } from "@iconify/react";
import { Block } from "../../../../shared";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IS_WEB } from "../../../../../utils/runtime";
import { logout } from "../../../../../services/login";

const SUPPORT_URL =
  process.env.REACT_APP_SUPPORT_URL || "https://t.me/zero_wallet_support";

interface RowProps {
  icon: string;
  label: string;
  onClick: () => void;
  hideDivider?: boolean;
}

/**
 * Один пункт секции. Иконка + текст + шеврон выровнены по центру строки.
 * Разделитель между пунктами — отдельный `Box` под строкой, чтобы не
 * ломать вертикальный ритм внутренними paddings у `ListItem`.
 */
function Row({ icon, label, onClick, hideDivider }: RowProps) {
  const theme = useTheme();
  const dividerColor =
    theme.palette.mode === "dark" ? "#26262A" : "rgba(239, 239, 243, 1)";
  return (
    <>
      <ListItem
        onClick={onClick}
        sx={{
          paddingInline: 0,
          paddingBlock: "8px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          cursor: "pointer",
        }}
      >
        <ListItemIcon
          sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(51,119,255,0.16)"
                : "rgba(51,119,255,0.10)",
            color: theme.palette.primary.main,
            minWidth: "36px",
            width: "36px",
            height: "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "10px",
            flexShrink: 0,
          })}
        >
          <Icon icon={icon} width={20} height={20} color="currentColor" />
        </ListItemIcon>

        <ListItemText
          sx={{ margin: 0, flexGrow: 1 }}
          primary={
            <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>
              {label}
            </Typography>
          }
        />

        <ChevronRight sx={{ color: "#b0b0b0", flexShrink: 0 }} />
      </ListItem>
      {!hideDivider && (
        <Box
          sx={{
            height: "1px",
            backgroundColor: dividerColor,
            marginLeft: "48px",
          }}
        />
      )}
    </>
  );
}

export function GeneralNavigationBlock() {
  const navigate = useNavigate();
  const { t } = useTranslation("profile");
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      // После logout()  cookie отозвано, access-token удалён.
      // Reload — чтобы RootLayout заново пошёл в getAccessToken(),
      // получил null и показал WebAuthScreen.
      window.location.replace("/");
    }
  };

  return (
    <>
      <Block sx={{ paddingTop: "4px", paddingBottom: "4px" }}>
        <Row
          icon="solar:info-circle-linear"
          label={t("blocks.about")}
          onClick={() => navigate("/about")}
        />
        <Row
          icon="solar:question-circle-linear"
          label={t("blocks.faq")}
          onClick={() => navigate("/faq")}
        />
        {/* «Устройства» показываем и в TG, и в вебе — юзер должен
            видеть все свои активные веб-сеансы, даже если сейчас
            зашёл через Telegram, чтобы завершить подозрительный.
            В самом /sessions виртуальная строка «Telegram Mini App»
            рендерится сверху со статусом «Текущее устройство». */}
        <Row
          icon="solar:devices-linear"
          label={t("blocks.sessions")}
          onClick={() => navigate("/sessions")}
        />
        <Row
          icon="solar:chat-round-dots-linear"
          label={t("blocks.support")}
          onClick={() =>
            window.Telegram?.WebApp?.openTelegramLink?.(SUPPORT_URL)
          }
          hideDivider={!IS_WEB}
        />
        {/* Logout — только в веб-режиме. В TG сессия привязана к
            initData, ревокать её бессмысленно (открытие бота создаёт
            новую), а «выйти» — просто закрыть mini app. */}
        {IS_WEB && (
          <Row
            icon="solar:logout-3-linear"
            label={t("blocks.logout")}
            onClick={() => setLogoutOpen(true)}
            hideDivider
          />
        )}
      </Block>

      <Dialog
        open={logoutOpen}
        onClose={() => !loggingOut && setLogoutOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("blocks.logout_confirm_title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("blocks.logout_confirm_text")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLogoutOpen(false)}
            disabled={loggingOut}
            sx={{ textTransform: "none" }}
          >
            {t("blocks.logout_cancel")}
          </Button>
          <Button
            onClick={handleLogout}
            disabled={loggingOut}
            color="error"
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            {t("blocks.logout_confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
